<template>
    <div class="activity-learn-page" v-loading="loading">
        <!-- Top Navbar -->
        <header class="learn-header">
            <div class="header-left">
                <el-button link class="back-btn" @click="goBack">
                    <el-icon>
                        <ArrowLeft />
                    </el-icon>
                    {{ activityTitle || '返回课程详情' }}
                </el-button>
            </div>
            <div class="header-center">
                <span class="node-title">{{ currentNode?.title || '加载中...' }}</span>
            </div>
            <div class="header-right">
                <!-- Placeholder for future settings/profile -->
                <el-tooltip content="设置" placement="bottom">
                    <el-button circle icon="Setting" />
                </el-tooltip>
            </div>
        </header>

        <!-- Main Workspace -->
        <main class="learn-workspace">
            <!-- Left Sidebar: Contents -->
            <aside class="sidebar-toc" :class="{ 'collapsed': !sidebarVisible }">
                <div class="sidebar-header">
                    <h3>课程大纲</h3>
                    <el-button link icon="Fold" @click="sidebarVisible = false" v-if="sidebarVisible" />
                    <el-button link icon="Expand" @click="sidebarVisible = true" v-else />
                </div>

                <el-scrollbar class="toc-scroll" v-show="sidebarVisible">
                    <!-- Custom Tree Rendering -->
                    <div v-for="(chapter, cIdx) in tocTree" :key="chapter.id" class="toc-chapter">
                        <div class="chapter-title" @click="toggleChapter(cIdx)">
                            <el-icon :class="{ 'is-open': chapter.isOpen }">
                                <CaretRight />
                            </el-icon>
                            <span>{{ chapter.title }}</span>
                        </div>

                        <el-collapse-transition>
                            <div v-show="chapter.isOpen" class="chapter-content">
                                <div v-for="section in chapter.children" :key="section.id" class="toc-section">
                                    <div class="section-title">{{ section.title }}</div>

                                    <div v-for="node in section.children" :key="node.id" class="toc-node"
                                        :class="{ 'is-active': currentNodeId === node.id }" @click="selectNode(node)">
                                        <el-icon>
                                            <Document />
                                        </el-icon>
                                        <span class="node-name">{{ node.title }}</span>
                                    </div>
                                </div>
                            </div>
                        </el-collapse-transition>
                    </div>
                </el-scrollbar>
            </aside>

            <!-- Center Content: Article/Paper -->
            <section class="main-content-area">
                <el-scrollbar class="content-scroll" ref="scrollContainer">
                    <div class="content-wrapper">
                        <!-- State 1: Loading/Empty -->
                        <div v-if="!currentNode" class="empty-state">
                            请在左侧选择要学习的内容
                        </div>

                        <!-- State 2: Plain File/Article (If we support rich text nodes directly later) -->
                        <div v-else-if="!currentNode.paperId" class="article-reader">
                            <!-- Currently nodes are mapped to papers, but this leaves room for Markdown lessons -->
                            <h2 class="lesson-title">{{ currentNode.title }}</h2>
                            <el-empty description="当前节点仅提供外部文件下载" v-if="currentNode.fileUrl">
                                <el-button type="primary" @click="downloadFile(currentNode.fileUrl)">下载内容</el-button>
                            </el-empty>
                        </div>

                        <!-- State 3: Paper/Quiz Display -->
                        <div v-else-if="currentPaper && currentPaper.questions" class="paper-reader">
                            <h2 class="paper-title">{{ currentPaper.title }}</h2>

                            <div class="question-list">
                                <div v-for="(q, index) in currentPaper.questions" :key="q.id"
                                    class="question-item box-card">
                                    <!-- Header: Type & Points -->
                                    <div class="q-header">
                                        <span class="q-num">{{ index + 1 }}.</span>
                                        <el-tag size="small" type="info">{{ getTypeName(q.type) }}</el-tag>
                                        <span class="q-points" v-if="q.points">({{ q.points }}分)</span>
                                    </div>

                                    <!-- The Question Body (LaTeX rendered via MdEditor preview) -->
                                    <div class="q-content">
                                        <md-editor v-model="q.content" preview-only />
                                    </div>

                                    <!-- Options (If Multiple Choice) -->
                                    <div v-if="q.type === 'single_choice' || q.type === 'multiple_choice'"
                                        class="q-options">
                                        <div v-for="opt in parseOptions(q.options)" :key="opt.key" class="option-row">
                                            <span class="opt-key">{{ opt.key }}.</span>
                                            <div class="opt-val"><md-editor v-model="opt.val" preview-only /></div>
                                        </div>
                                    </div>

                                    <!-- "Paper-First" Show Answer Toggle -->
                                    <div class="q-action-bar">
                                        <el-button class="reveal-btn" :type="revealState[q.id] ? 'info' : 'primary'"
                                            plain @click="toggleReveal(q.id)">
                                            {{ revealState[q.id] ? '收起解析' : '演算完成，查看解析' }}
                                        </el-button>
                                    </div>

                                    <!-- Answer & Analysis Area -->
                                    <el-collapse-transition>
                                        <div v-show="revealState[q.id]" class="q-analysis-area">
                                            <div class="analysis-block answer">
                                                <h4>正确答案</h4>
                                                <div class="ans-content"><md-editor v-model="q.answer" preview-only />
                                                </div>
                                            </div>
                                            <div class="analysis-block steps" v-if="q.analysis">
                                                <h4>详细解析</h4>
                                                <div class="ans-content"><md-editor v-model="q.analysis" preview-only />
                                                </div>
                                            </div>
                                            <div class="analysis-block none" v-else>
                                                <p>老师尚未提供该题的详细解析。</p>
                                            </div>
                                        </div>
                                    </el-collapse-transition>
                                </div>
                            </div>
                        </div>
                    </div>
                </el-scrollbar>

                <!-- Bottom Sticky Footer Control -->
                <div class="bottom-controls" v-if="currentNode">
                    <el-button :disabled="!hasPrev" @click="goPrev" icon="ArrowLeft">上一节</el-button>

                    <el-button type="success" plain @click="openCheckInDialog">
                        <el-icon class="el-icon--left">
                            <Check v-if="myCheckIn" />
                            <Edit v-else />
                        </el-icon>
                        {{ myCheckIn ? '已打卡，查看或修改' : '编辑记录 / 拍照打卡' }}
                    </el-button>

                    <el-button :disabled="!hasNext" type="primary" @click="goNext">
                        下一节 <el-icon class="el-icon--right">
                            <ArrowRight />
                        </el-icon>
                    </el-button>
                </div>
            </section>
        </main>

        <!-- Check-In Dialog -->
        <el-dialog v-model="showCheckInDialog" title="学习打卡" width="500px">
            <el-form label-position="top">
                <el-form-item label="打卡心得/演算过程">
                    <el-input v-model="checkInForm.content" type="textarea" :rows="4"
                        placeholder="写下你的思路、遇到的问题，或者简单的打卡宣言..."></el-input>
                </el-form-item>
                <el-form-item label="上传草稿/演算图片 (可选)">
                    <el-upload class="checkin-uploader" :action="getUploadAction()" :headers="uploadHeaders"
                        :data="{ type: 'checkin' }" :show-file-list="false" :on-success="handleCheckInUploadSuccess"
                        :before-upload="beforeUpload">
                        <img v-if="checkInForm.imageUrl" :src="checkInForm.imageUrl" class="uploaded-img" />
                        <el-icon v-else class="uploader-icon">
                            <Plus />
                        </el-icon>
                    </el-upload>
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="showCheckInDialog = false">取消</el-button>
                    <el-button type="primary" @click="submitCheckIn" :loading="submittingCheckIn">
                        提交打卡
                    </el-button>
                </span>
            </template>
        </el-dialog>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft, ArrowRight, Document, CaretRight, Fold, Expand, Setting, Check, Edit, Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import api from '@/utils/api'
import MdEditor from 'md-editor-v3'

const route = useRoute()
const router = useRouter()
const activityId = route.params.id

const loading = ref(false)
const activityTitle = ref('')
const tocTree = ref([])

const currentNodeId = ref(null)
const currentNode = ref(null)
const currentPaper = ref(null)

const sidebarVisible = ref(true)
const scrollContainer = ref(null)

// State for revealing answers per question ID
const revealState = ref({})

// Flattened node list used for Prev/Next navigation
const flatNodes = ref([])

// Check-In Feature
const myCheckIn = ref(null)
const showCheckInDialog = ref(false)
const checkInForm = ref({ content: '', imageUrl: '' })
const submittingCheckIn = ref(false)

const getUploadAction = () => {
    return (api.defaults.baseURL || '') + '/api/upload'
}
const uploadHeaders = computed(() => {
    return { Authorization: `Bearer ${localStorage.getItem('token')}` }
})

onMounted(async () => {
    loading.value = true
    try {
        // 1. Fetch Activity outline
        const [actRes, nodeRes] = await Promise.all([
            api.get(`/api/activities/${activityId}`),
            api.get(`/api/activities/${activityId}/nodes`)
        ])

        // Ensure user is enrolled before letting them view learning content
        if (!actRes.data.isEnrolled) {
            ElMessage.error('请先报名该活动才能进入学习模式')
            router.replace(`/activities/${activityId}`)
            return
        }

        activityTitle.value = actRes.data.activity.title

        // Setup TOC state (expand first chapter by default)
        tocTree.value = (nodeRes.data.nodes || []).map((chap, idx) => ({
            ...chap,
            isOpen: idx === 0
        }))

        // Flatten the tree to find Prev/Next flows
        buildFlatNodeList(tocTree.value)

        // Read query param to resume learning state, or default to very first node
        const qNodeId = Number(route.query.nodeId)
        if (qNodeId) {
            const target = flatNodes.value.find(n => n.id === qNodeId)
            if (target) {
                target.chapterRef.isOpen = true // ensure parent is expanded
                await selectNode(target.node)
            } else if (flatNodes.value.length > 0) {
                await selectNode(flatNodes.value[0].node)
            }
        } else if (flatNodes.value.length > 0) {
            await selectNode(flatNodes.value[0].node)
        }

    } catch (e) {
        ElMessage.error('获取课程数据失败')
        console.error(e)
    } finally {
        loading.value = false
    }
})

// Build a 1D array of traversable nodes that contain content (Level 3 nodes)
const buildFlatNodeList = (chapters) => {
    flatNodes.value = []
    chapters.forEach(ch => {
        if (!ch.children) return
        ch.children.forEach(sec => {
            if (!sec.children) return
            sec.children.forEach(node => {
                flatNodes.value.push({
                    id: node.id,
                    node: node,
                    chapterRef: ch
                })
            })
        })
    })
}

// Node Selection
const selectNode = async (node) => {
    currentNodeId.value = node.id
    currentNode.value = node

    // Update URL query silently so refreshing remembers position
    router.replace({ query: { ...route.query, nodeId: node.id } })

    // Reset reveal states
    revealState.value = {}

    // Scroll to top
    if (scrollContainer.value) {
        scrollContainer.value.setScrollTop(0)
    }

    if (node.paperId) {
        await fetchPaperDetails(node.paperId)
    } else {
        currentPaper.value = null
    }

    await fetchCheckIn(node.id)
}

const fetchCheckIn = async (nodeId) => {
    try {
        const res = await api.get(`/api/activities/${activityId}/nodes/${nodeId}/checkin`)
        myCheckIn.value = res.data.checkIn
        if (myCheckIn.value) {
            checkInForm.value = {
                content: myCheckIn.value.content,
                imageUrl: myCheckIn.value.imageUrl
            }
        } else {
            checkInForm.value = { content: '', imageUrl: '' }
        }
    } catch (e) { console.error(e) }
}

const fetchPaperDetails = async (paperId) => {
    loading.value = true
    try {
        const res = await api.get(`/api/papers/${paperId}`)
        const paperData = res.data
        if (paperData.paperQuestions) {
            paperData.questions = paperData.paperQuestions.map(pq => pq.question).filter(q => !!q)
        } else {
            paperData.questions = []
        }
        currentPaper.value = paperData
    } catch (e) {
        ElMessage.error('加载题目失败')
    } finally {
        loading.value = false
    }
}

// Navigation Logic
const currentIndex = computed(() => flatNodes.value.findIndex(n => n.id === currentNodeId.value))
const hasPrev = computed(() => currentIndex.value > 0)
const hasNext = computed(() => currentIndex.value < flatNodes.value.length - 1)

const goPrev = () => {
    if (hasPrev.value) {
        const target = flatNodes.value[currentIndex.value - 1]
        target.chapterRef.isOpen = true
        selectNode(target.node)
    }
}

const goNext = () => {
    if (hasNext.value) {
        const target = flatNodes.value[currentIndex.value + 1]
        target.chapterRef.isOpen = true
        selectNode(target.node)
    }
}

const toggleChapter = (idx) => {
    tocTree.value[idx].isOpen = !tocTree.value[idx].isOpen
}

const toggleReveal = (qId) => {
    revealState.value[qId] = !revealState.value[qId]
}

const goBack = () => {
    router.push(`/activities/${activityId}`)
}

const downloadFile = (url) => {
    window.open(url, '_blank')
}

// Utility formatting
const getTypeName = (type) => {
    const map = {
        'single_choice': '单选题',
        'multiple_choice': '多选题',
        'fill_in_blank': '填空题',
        'subjective': '解答题'
    }
    return map[type] || '题目'
}

const parseOptions = (jsonStr) => {
    if (!jsonStr) return []
    try {
        const arr = JSON.parse(jsonStr)
        // Ensure options format [{key: 'A', val: 'text'}, ...]
        return arr.map(item => typeof item === 'string' ? JSON.parse(item) : item)
    } catch (e) {
        return []
    }
}

// Check-In Actions
const openCheckInDialog = () => {
    showCheckInDialog.value = true
}

const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
        ElMessage.error('只能上传图片文件!')
    }
    return isImage
}

const handleCheckInUploadSuccess = (res) => {
    if (res.url || res.data?.url) {
        checkInForm.value.imageUrl = res.url || res.data?.url
        ElMessage.success('图片上传成功')
    } else {
        ElMessage.error('图片获取失败')
    }
}

const submitCheckIn = async () => {
    if (!checkInForm.value.content && !checkInForm.value.imageUrl) {
        return ElMessage.warning('请输入内容或上传图片才能打卡')
    }
    submittingCheckIn.value = true
    try {
        await api.post(`/api/activities/${activityId}/nodes/${currentNodeId.value}/checkin`, {
            content: checkInForm.value.content,
            imageUrl: checkInForm.value.imageUrl
        })
        ElMessage.success('打卡成功')
        showCheckInDialog.value = false
        await fetchCheckIn(currentNodeId.value)
    } catch (e) {
        ElMessage.error('打卡请求失败')
    } finally {
        submittingCheckIn.value = false
    }
}

</script>

<style scoped>
.activity-learn-page {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: #fafafa;
    overflow: hidden;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Header */
.learn-header {
    height: 60px;
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    z-index: 100;
}

.header-left {
    width: 250px;
}

.back-btn {
    font-size: 14px;
    color: #4b5563;
    font-weight: 500;
}

.header-center {
    flex: 1;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.node-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
}

.header-right {
    width: 250px;
    display: flex;
    justify-content: flex-end;
}

/* Workspace */
.learn-workspace {
    flex: 1;
    display: flex;
    overflow: hidden;
}

/* Sidebar */
.sidebar-toc {
    width: 320px;
    background: #ffffff;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-toc.collapsed {
    width: 0;
    border-right: none;
    opacity: 0;
}

.sidebar-header {
    padding: 16px 20px;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.sidebar-header h3 {
    margin: 0;
    font-size: 15px;
    color: #374151;
}

.toc-scroll {
    flex: 1;
}

.toc-chapter {
    border-bottom: 1px solid #f9fafb;
}

.chapter-title {
    padding: 14px 20px;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
}

.chapter-title:hover {
    background: #f9fafb;
}

.chapter-title .el-icon {
    font-size: 12px;
    color: #9ca3af;
    transition: transform 0.2s;
}

.chapter-title .el-icon.is-open {
    transform: rotate(90deg);
}

.chapter-content {
    background: #fafafa;
    padding-bottom: 8px;
}

.toc-section {
    padding: 8px 0;
}

.section-title {
    padding: 4px 20px 4px 40px;
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
}

.toc-node {
    padding: 10px 20px 10px 48px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #4b5563;
    transition: all 0.2s;
    font-size: 14px;
}

.toc-node:hover {
    background: #f3f4f6;
}

.toc-node.is-active {
    background: #eff6ff;
    color: #2563eb;
    font-weight: 500;
    border-right: 3px solid #2563eb;
}

.toc-node .el-icon {
    font-size: 16px;
    opacity: 0.7;
}

/* Center Content Area */
.main-content-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f3f4f6;
    /* Subtle dark behind the paper */
    position: relative;
    overflow: hidden;
    /* Needed for inner scroll to work */
}

.content-scroll {
    flex: 1;
}

.content-wrapper {
    max-width: 900px;
    margin: 0 auto;
    padding: 40px 24px 80px;
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    color: #9ca3af;
    font-size: 16px;
}

.paper-reader .paper-title {
    font-size: 28px;
    margin-bottom: 32px;
    color: #1f2937;
    text-align: center;
}

/* Paper-style Question Card */
.box-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    padding: 32px;
    margin-bottom: 32px;
}

.q-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.q-num {
    font-size: 18px;
    font-weight: bold;
    color: #1f2937;
}

.q-points {
    color: #6b7280;
    font-size: 14px;
}

.q-content {
    font-size: 16px;
    color: #374151;
    line-height: 1.6;
    margin-bottom: 24px;
}

/* Override md-editor internal spacing for seamless paper look */
.q-content :deep(.md-editor-preview p) {
    margin-bottom: 0;
}

.q-options {
    margin-bottom: 32px;
    padding-left: 24px;
}

.option-row {
    display: flex;
    margin-bottom: 12px;
    align-items: flex-start;
}

.opt-key {
    font-weight: bold;
    margin-right: 12px;
    min-width: 24px;
    padding-top: 2px;
}

/* Action Bar below Question */
.q-action-bar {
    display: flex;
    justify-content: center;
    padding-top: 24px;
    border-top: 1px dashed #e5e7eb;
}

.reveal-btn {
    border-radius: 999px;
    padding: 10px 32px;
    font-weight: 500;
}

/* Analysis Dropdown */
.q-analysis-area {
    margin-top: 24px;
    background: #f8fafc;
    border-radius: 8px;
    padding: 24px;
    border-left: 4px solid #10b981;
    /* Green rim for solutions */
}

.analysis-block {
    margin-bottom: 20px;
}

.analysis-block:last-child {
    margin-bottom: 0;
}

.analysis-block h4 {
    margin: 0 0 12px 0;
    color: #1f2937;
    font-size: 15px;
    font-weight: 600;
}

.ans-content {
    font-size: 15px;
    color: #4b5563;
    line-height: 1.6;
}

.analysis-block.none p {
    color: #9ca3af;
    font-style: italic;
    margin: 0;
}

.bottom-controls {
    height: 70px;
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 40px;
    box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.02);
}

/* Upload Check-in styles */
:deep(.checkin-uploader .el-upload) {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    width: 140px;
    height: 140px;
    display: flex;
    justify-content: center;
    align-items: center;
}

:deep(.checkin-uploader .el-upload:hover) {
    border-color: #409eff;
}

.uploader-icon {
    font-size: 28px;
    color: #8c939d;
}

.uploaded-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
</style>
